import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { MdVisibility, MdLogout } from 'react-icons/md';
import { impersonationContext, stopImpersonation, onImpersonationChange } from 'helpers/impersonation';
import resetPersistedState from 'helpers/resetPersistedState';
import { persistor } from 'redux/store';

/**
 * A permanent bar across the top while an administrator is working as somebody
 * else.
 *
 * Deliberately loud, and deliberately impossible to dismiss. The failure mode
 * this prevents is the whole reason impersonation is risky: an administrator
 * forgets which identity they are in, keeps working, and every record they touch
 * carries a colleague's name. A quiet indicator gets tuned out within a day; a
 * red bar with the person's name in it does not.
 */
// Its height, as --impersonation-height on <html>: the sticky top navbar, the sidebar
// and the fixed page cards sit below it instead of under it (they overlapped, 2026-10-22).
const useBannerHeight = (ref, active) => {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const node = ref.current;
    if (!active || !node) {
      root.style.removeProperty('--impersonation-height');
      return undefined;
    }
    const publish = () => root.style.setProperty('--impersonation-height', `${node.getBoundingClientRect().height}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(node);
    window.addEventListener('resize', publish);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', publish);
      root.style.removeProperty('--impersonation-height');
    };
  }, [ref, active]);
};

const ImpersonationBanner = () => {
  const ref = useRef(null);
  const context = impersonationContext();
  useBannerHeight(ref, !!context);
  // View-as started or stopped in another tab: the tokens are shared, so this tab
  // reloads as the new identity (a stale banner here used to sign the admin out).
  useEffect(() => onImpersonationChange(async () => {
    await resetPersistedState(persistor);
    window.location.reload();
  }), []);
  if (!context) return null;

  const handleStop = async () => {
    const restored = stopImpersonation();
    // The persisted store holds the viewed person's records — never carry them back.
    await resetPersistedState(persistor);

    // A full reload either way: every slice in the store was fetched as the
    // other person, and carrying any of it back would show their data under the
    // administrator's name.
    window.location.href = restored ? '/' : '/login';
  };

  return (
    <div
      ref={ref}
      role="alert"
      className="impersonation-banner tw-sticky tw-top-0 tw-z-[100001] tw-flex tw-items-center tw-justify-center tw-gap-3 tw-px-4 tw-py-2 tw-bg-red-600 tw-text-white tw-text-sm"
    >
      <MdVisibility size={17} className="tw-shrink-0" />
      <span>
        You are viewing the portal as <strong>{context.target?.name}</strong>
        {context.target?.email && <span className="tw-opacity-80"> ({context.target.email})</span>}.
        Everything you do is recorded against your own name.
      </span>
      <button
        type="button"
        onClick={handleStop}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-md tw-bg-white tw-text-red-700 tw-text-xs tw-font-bold tw-border-0 hover:tw-bg-red-50 tw-transition-colors tw-shrink-0"
      >
        <MdLogout size={14} /> Stop
      </button>
    </div>
  );
};

export default ImpersonationBanner;
